
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import slugify from "slugify";
import uniqueSlug from "unique-slug";

import GET_PROJECT_SKILLS from "@/apollo/skills/GetProjectSkills.gql";

import ManagersMultiselect from "@/components/autocompleteMultiselects/ManagersMultiselect.vue";
import InputFilter from "@/components/filters/InputFilter.vue";
import FormInputGroup from "@/components/form/FormInputGroup.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import FormDatepicker from "@/components/form/FormDatepicker.vue";
import FormGroup from "@/components/form/FormGroup.vue";
import RouteNames from "@/entities/RouteNames";
import CREATE_PROJECT from "@/apollo/projects/CreateProject.gql";
import UPDATE_PROJECT from "@/apollo/projects/UpdateProject.gql";
import UIButton from "@/components/button/ui-button.vue";
import ModalFullScreen from "@/components/modal/ModalFullScreen.vue";
import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
import ProfileCountrySelect from "@/components/autocompleteMultiselects/ProfileCountrySelect.vue";

import {
  Project_Insert_Input,
  Project_Set_Input,
  Project_Skill,
  User,
} from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import Roles from "@/entities/Roles";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import CustomSkillsMultiselect from "@/components/autocompleteMultiselects/customOptions/CustomSkillsMultiselect.vue";
import SkillMultiselectItem from "@/components/skills/SkillMultiselectItem.vue";
import SkillMultiselectOption from "@/entities/SkillMultiselectOption";
import FormError from "@/components/common/FormError.vue";
import DropdownOption from "@/entities/DropdownOption";
import RegionInput from "@/components/autocompleteMultiselects/RegionInput.vue";
import ProjectDataForModal from "@/entities/modals/ProjectDataForModal";

export interface ProjectForModal {
  name: string | null | undefined;
  country?: DropdownOption[];
  state?: DropdownOption[];
  city?: string | null;
  start_date: string | Date;
  end_date: string | Date;
  min_budget: number | null;
  max_budget: number | null;
  description: string;
  managersIds: string[];
  id?: null | string;
  company_id?: null | string;
}

export interface ProjectForDatabase {
  name: string;
  country: string | null;
  state?: string | null;
  city?: string | null;
  start_date: string | Date;
  end_date: string;
  min_budget: number | null;
  max_budget: number | null;
  description: string | null;
  id?: null | string;
  company_id: string;
  manager_id?: string;
  skillsIds?: number[];
}

interface UpdatePersonalSkills {
  id: number;
  user_id: null;
  company_id: string;
}

interface UpdateProjectArgs {
  set: Project_Set_Input;
  projectId: string;
  addSkills: {
    project_id: string;
    skill_id: number;
  }[];
  updatePersonalSkills: UpdatePersonalSkills[];
  deleteSkillsIds: number[];
  customSkills: {
    name: string;
    company_id: string;
    project_skills: {
      data: {
        project_id: string;
      };
    };
  }[];
}

interface ProjectArgs {
  skillIds: number[];
  companyId: string;
  userId: string;
  updatedProject: Project_Insert_Input;
}

@Component({
  components: {
    RegionInput,
    FormError,
    SkillMultiselectItem,
    CustomSkillsMultiselect,
    LoadingOverlay,
    ManagersMultiselect,
    InputFilter,
    FormTextarea,
    FormGroup,
    FormDatepicker,
    FormInputGroup,
    ModalFullScreen,
    UIButton,
    ModalSectionsDivider,
    ProfileCountrySelect,
  },
  apollo: {
    skillsIds: {
      query: GET_PROJECT_SKILLS,
      variables() {
        return {
          projectId: this.data.id,
        };
      },
      update(data): void {
        const skillsIds = data.project_skill.map(
          ({ skill_id }: Project_Skill) => skill_id
        );
        this.projectSkillsIds = skillsIds;
        this.editedProjectSkillsIds = skillsIds;
      },
      skip(): boolean {
        return !Boolean(this.data?.id);
      },
    },
  },
})
export default class ProjectModal extends Vue {
  @Prop({ type: Object, default: null })
  readonly data!: ProjectDataForModal | null;
  @Prop({ type: Boolean, default: false }) readonly dialog!: boolean;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Boolean, default: false }) readonly isEdittingLocked!: boolean;

  valid: boolean = false;
  projectSkillsIds: number[] = [];
  editedProjectSkillsIds: number[] = [];
  newSkillsNames: string[] = [];
  personalSkills: SkillMultiselectOption[] = [];
  countryFromDB: string | null = "";

  project: ProjectForModal = {
    name: null,
    country: [],
    state: [],
    city: "",
    start_date: "",
    end_date: "",
    managersIds: [],
    min_budget: null,
    max_budget: null,
    description: "",
    id: null,
    company_id: null,
  };

  mounted() {
    if (this.data) {
      const managersIds = this.data.manager?.id ? [this.data.manager?.id] : [];
      const min_budget = this.data?.min_budget || null;
      const max_budget = this.data?.max_budget || null;
      const country = this.data.country;
      const state = this.data.state;
      this.countryFromDB = country || null;
      this.project = {
        name: this.data.name,
        country: country ? [{ id: country, name: country }] : [],
        state: state ? [{ id: state, name: state }] : [],
        city: this.data.city,
        start_date: this.data.start_date,
        end_date: this.data.end_date,
        managersIds,
        min_budget,
        max_budget,
        description: this.data.description || "",
        id: this.data.id,
      };
    }
  }

  @Watch("countryName", { immediate: false })
  clearCountry(newCountry: string): void {
    if (newCountry !== this.countryFromDB || !Boolean(newCountry)) {
      this.project.state = [];
      this.project.city = "";
    }
  }

  get countryName(): string {
    if (!this.project.country?.length) {
      return "";
    }
    return this.project.country[0].name;
  }

  get personalSkillsChosenWarning(): string {
    if (this.personalSkills.length === 0) {
      return "";
    }

    return this.personalSkills.map(({ name }) => name).join(", ");
  }

  get modalTitle(): string {
    return `${this.modalPurpose} Client`;
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get projectId(): string | undefined {
    return this.data?.id;
  }

  get isCreatedProject(): boolean {
    return Boolean(this.data && !this.data.isNew);
  }

  get modalPurpose(): string {
    return this.isCreatedProject ? "Edit" : "Create";
  }

  get buttonText(): string {
    return this.isCreatedProject ? "Update" : "Create";
  }

  get selectedManagersIds(): string[] {
    return this.project.managersIds;
  }

  get countryForDb(): string | null {
    if (!this.project.country?.length) {
      return null;
    }
    return this.project.country[0].name;
  }

  get stateForDb(): string | null {
    if (!this.project.state?.length) {
      return null;
    }
    return this.project.state[0].name;
  }

  get projectToSave(): ProjectForDatabase {
    return {
      name: this.project.name!,
      start_date: this.project.start_date,
      end_date: this.project.end_date as string,
      manager_id: this.currentUser?.manager
        ? this.currentUser.manager.id
        : this.project.managersIds[0],
      min_budget: this.project.min_budget || null,
      max_budget: this.project.max_budget || null,
      country: this.countryForDb,
      state: this.stateForDb,
      city: this.project.city || null,
      description: this.project.description,
      company_id: this.companyId,
      skillsIds: this.editedProjectSkillsIds,
    };
  }

  isPersonalSkill(skill: SkillMultiselectOption): boolean {
    return skill.user_id === this.currentUser.id;
  }

  handleManagerInput(managersIds: string[]): void {
    this.project = {
      ...this.project,
      managersIds,
    };
  }

  updateSkills(
    skillsIds: number[],
    newSkillsNames: string[],
    allChosenSkills: SkillMultiselectOption[]
  ): void {
    this.editedProjectSkillsIds = skillsIds;
    this.newSkillsNames = newSkillsNames;
    this.personalSkills = allChosenSkills.filter((skill) =>
      Boolean(skill.user_id)
    );
  }

  handleSkillInput(skillsIds: number[]): void {
    this.editedProjectSkillsIds = skillsIds;
  }

  onSubmit(): void {
    if (this.isCreatedProject) {
      this.handleUpdate({ ...this.projectToSave, id: this.project?.id });
    } else {
      this.handleCreate(this.projectToSave);
    }
  }

  closeModal(): void {
    this.$root.$emit("projectsChanged");
    this.$emit("close");
  }

  // update and create
  async handleUpdate(project: ProjectForDatabase): Promise<void> {
    try {
      const projectId = project.id!;
      const updatedProject = await this._updateProject({
        set: {
          company_id: project.company_id,
          slug: project.name
            ? `${slugify(project.name, { lower: true })}-${uniqueSlug()}`
            : null,
          name: project.name,
          start_date: project.start_date || null,
          end_date: project.end_date || null,
          manager_id: project.manager_id || null,
          min_budget: project.min_budget,
          max_budget: project.max_budget,
          country: this.countryForDb,
          state: this.stateForDb,
          city: project.city || null,
          description: project.description,
        },
        projectId,
        deleteSkillsIds: this.projectSkillsIds,
        addSkills: this.editedProjectSkillsIds.map((skillId) => ({
          project_id: projectId,
          skill_id: skillId,
        })),
        updatePersonalSkills: this.personalSkills.map(({ id }) => ({
          id,
          user_id: null,
          company_id: this.companyId,
        })),
        customSkills: this.newSkillsNames.map((skillName) => ({
          name: skillName,
          company_id: this.companyId,
          project_skills: {
            data: {
              project_id: projectId,
            },
          },
        })),
      });

      this.$emit("projectUpdated", updatedProject);
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Updated successfully")
      );
      this.closeModal();
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  async _updateProject(updatedProject: UpdateProjectArgs) {
    return await this.$apollo.mutate({
      mutation: UPDATE_PROJECT,
      variables: updatedProject,
    });
  }

  async handleCreate(project: ProjectForDatabase): Promise<void> {
    try {
      const response = await this._createProject({
        skillIds: this.editedProjectSkillsIds.map((skillId) => Number(skillId)),
        companyId: this.companyId,
        userId: this.currentUser.id,
        updatedProject: {
          company_id: project.company_id,
          slug: project.name,
          name: project.name,
          start_date: project.start_date || null,
          end_date: project.end_date || null,
          manager_id: project.manager_id,
          min_budget: project.min_budget,
          max_budget: project.max_budget,
          country: project.country,
          state: project.state,
          city: project.city,
          description: project.description,
          project_skills: {
            data: [
              ...this.editedProjectSkillsIds.map((skillId) => ({
                skill_id: skillId,
              })),
              ...this.newSkillsNames.map((skillName) => ({
                skill: {
                  data: {
                    name: skillName,
                    company_id: this.companyId,
                  },
                },
              })),
            ],
          },
        },
      });
      const { insert_project_one } = response.data;
      this.closeModal();
      this.$router.push({
        name: RouteNames.PROJECT_ACTIVITY,
        params: {
          id: insert_project_one.id,
        },
      });
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  async _createProject(variables: ProjectArgs) {
    return await this.$apollo.mutate({
      mutation: CREATE_PROJECT,
      variables,
    });
  }
}
